/**
 * ExistingCompanyPicker — sélecteur strict d'une company EXISTANTE du tenant
 * (Chunk 9.T.4 fix post-smoke).
 *
 * ⚠️ Distinct de `CompanyPicker` (9.T.2 anti-doublon) :
 *
 *   | Composant                | Endpoint                          | Sémantique                         |
 *   | ------------------------ | --------------------------------- | ---------------------------------- |
 *   | `CompanyPicker`          | `GET /v1/companies/lookup`        | Anti-doublon avant création        |
 *   |                          | (trigram + booster postal +       | (used by wizard `/projects/new`    |
 *   |                          | penalty postal + floor 0.6)       | Step1Company)                      |
 *   | `ExistingCompanyPicker`  | `GET /v1/companies?q=…`           | Sélection existante fiable         |
 *   |                          | (ILIKE simple legal_name/         | (used by MaterializeEventDialog    |
 *   |                          | trade_name/business_number)       | stratégie 2 attach_company_create) |
 *
 * Pourquoi une distinction ?
 * --------------------------
 * Le lookup 9.T.2 est conçu pour détecter des doublons avec seuil trigram
 * strict (`similarity >= 0.6`). Il rate volontairement les recherches courtes
 * ou trop génériques pour éviter de sur-suggérer des matches faibles au
 * broker qui s'apprête à créer.
 *
 * Le contexte materialize stratégie 2 (« attach_company_create_project ») est
 * DIFFÉRENT : le broker sait qu'il veut sélectionner une company déjà en DB.
 * Il tape `9444` pour retrouver `9444-1111 Qc inc` et doit la trouver du
 * premier coup — le seuil trigram n'a pas de sens ici. Un ILIKE simple sur
 * les 3 colonnes textuelles suffit et matche exactement l'attente broker.
 *
 * Design :
 *   - Input texte + debounce 300ms + dropdown résultats (parité UX
 *     `CompanyPicker` pour cohérence visuelle).
 *   - Pas de badge confidence (pas de scoring — c'est une simple liste).
 *   - Pas de CTA « Créer nouvelle entreprise » (la création est ailleurs :
 *     stratégie 3 du materialize dialog).
 *   - Status `all` (inclut archived) : le broker peut vouloir attacher une
 *     soumission à un projet sur un client archivé (audit historique).
 *     Un badge `archived` visible sur les items concernés le signale.
 *   - Empty state neutre : « No matching client found. Refine your search. »
 *     Pas de suggestion pour créer (contexte materialize gère ça via
 *     bascule stratégie 3 côté parent).
 */

import type { CompanyRow } from "@kollectivia/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCompanies, useCompany } from "../lib/queries/companies";

type Props = {
  /** UUID company sélectionnée, ou undefined. */
  value: string | undefined;
  onChange: (companyId: string | undefined) => void;
  /** Label du formulaire pour a11y. */
  id?: string;
  disabled?: boolean;
};

const DEBOUNCE_MS = 300;
const MIN_CHARS = 2;
const RESULT_LIMIT = 8;

export function ExistingCompanyPicker({ value, onChange, id, disabled }: Props) {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [open, setOpen] = useState(false);

  const selected = useCompany(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQ(q), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q]);

  const ready = open && debouncedQ.trim().length >= MIN_CHARS;
  const results = useCompanies(
    ready ? { q: debouncedQ.trim(), limit: RESULT_LIMIT, status: "all" } : {},
  );

  useEffect(() => {
    if (!value) setQ("");
  }, [value]);

  // Affichage lecture seule quand une company est déjà sélectionnée.
  if (value && selected.data) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-medium text-slate-900">{selected.data.legal_name}</div>
            {selected.data.address_city && (
              <div className="text-xs text-slate-500">{selected.data.address_city}</div>
            )}
          </div>
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => {
              onChange(undefined);
              setOpen(true);
            }}
            disabled={disabled}
          >
            {t("common.change")}
          </button>
        </div>
      </div>
    );
  }

  const items = (results.data?.items ?? []) as ReadonlyArray<CompanyRow>;
  const showEmptyState = ready && !results.isLoading && items.length === 0;

  return (
    <div>
      <div className="flex gap-2">
        <input
          id={id}
          type="search"
          className="input"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={t("companies.existing_picker.placeholder")}
          disabled={disabled}
          autoComplete="off"
          aria-expanded={open}
          aria-haspopup="listbox"
        />
      </div>

      {open && ready && (
        <div
          className="mt-1 max-h-72 overflow-auto rounded-lg border border-slate-200 bg-white shadow-sm"
          role="listbox"
          aria-label={t("companies.existing_picker.aria_listbox_label")}
        >
          {results.isLoading && !results.data ? (
            <div className="p-3 text-sm text-slate-400">{t("app.loading")}</div>
          ) : showEmptyState ? (
            <div className="space-y-1 p-3" data-testid="existing-company-picker-empty">
              <p className="text-sm text-slate-600">{t("companies.existing_picker.empty.title")}</p>
              <p className="text-xs text-slate-500">
                {t("companies.existing_picker.empty.subtitle")}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected="false"
                    className="block w-full px-3 py-2 text-left hover:bg-slate-50 focus-visible:bg-slate-100 focus-visible:outline-none"
                    data-testid={`existing-company-picker-item-${c.id}`}
                    onClick={() => {
                      onChange(c.id);
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900">{c.legal_name}</div>
                        <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                          {c.address_city && <span>{c.address_city}</span>}
                          {c.business_number && (
                            <span className="font-mono" translate="no">
                              {c.business_number}
                            </span>
                          )}
                        </div>
                      </div>
                      {c.status === "archived" && (
                        <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-inset ring-slate-200">
                          {t("companies.existing_picker.status_archived")}
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {open && !ready && (
        <p className="mt-1 text-xs text-slate-400">
          {t("common.search_min_chars", { count: MIN_CHARS })}
        </p>
      )}
    </div>
  );
}
